import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import { useRef, useState, useEffect } from "react";
import { Box, InputAdornment } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { SpaceXl } from "@nxt-costco-com/forge-design-tokens";
import { InputError } from "../InputError/index.js";
import { COMPONENT_NAME, COUNTDOWN_START_ANNOUNCE_LIMIT } from "./constants.js";
import { ErrorsContainer, StyledPlainText, StyledHelpLink, TextFieldMainContainer, TextFieldRowContainer, StyledTextField, TooltipContainer } from "./styles.js";
import { getDataTestId } from "../../Util/index.js";
const TextField = ({
  autoComplete = "",
  characterCounterText,
  charactersRemaining,
  endAdornment,
  endAdornmentId = "",
  errorText,
  errorTextId,
  errorIconAriaLabel = "Error: ",
  helpText,
  helpTextId,
  helpLinkText,
  helpLinkUrl,
  id,
  inputAriaLabel,
  inputAriaLabelledBy,
  inputLabelId,
  inputMode,
  inputRef,
  inputSize,
  isMultiline = false,
  isError = false,
  isRequired = true,
  label,
  labelOptionalText,
  maxCharacterLimit,
  maxRows,
  maxValue,
  minRows = 2,
  minValue,
  name,
  onBlur,
  onChange,
  onKeyDown,
  startAdornment,
  startAdornmentId = "",
  sx,
  tooltip,
  uniqueId,
  type,
  value = "",
  isDynamicHelpText = false
}) => {
  const srOnlyRef = useRef(null);
  const [endAdornmentWidth, setEndAdornmentWidth] = useState(0);
  const [srOnlyHelpText, _setsrOnlyHelpText] = useState(helpText);
  const endAdornmentRef = useRef(null);
  const fieldLabel = `${label}${!isRequired && labelOptionalText ? ` ${labelOptionalText}` : ""}`;
  const inputId = uniqueId ? `${uniqueId}_${id}` : id;
  useEffect(() => {
    var _a;
    setEndAdornmentWidth(((_a = endAdornmentRef == null ? void 0 : endAdornmentRef.current) == null ? void 0 : _a.offsetWidth) || 0);
  }, []);
  useEffect(() => {
    if (!(srOnlyRef == null ? void 0 : srOnlyRef.current) || !helpText || typeof charactersRemaining !== "number")
      return;
    if (charactersRemaining > 15) {
      srOnlyRef.current.removeAttribute("aria-live");
      srOnlyRef.current.removeAttribute("aria-atomic");
    }
    if (charactersRemaining <= 15 && charactersRemaining > 10) {
      srOnlyRef.current.setAttribute("aria-live", "polite");
      srOnlyRef.current.setAttribute("aria-atomic", "true");
    }
    if (charactersRemaining < 11 && charactersRemaining > 5) {
      srOnlyRef.current.setAttribute("aria-atomic", "false");
      srOnlyRef.current.setAttribute("aria-live", "polite");
    }
    if (charactersRemaining < 6) {
      srOnlyRef.current.setAttribute("aria-live", "assertive");
      srOnlyRef.current.setAttribute("aria-atomic", "true");
    }
    const timer = setTimeout(() => {
      if (!(srOnlyRef == null ? void 0 : srOnlyRef.current)) return;
      srOnlyRef.current.textContent = helpText;
    }, 1e3);
    return () => clearTimeout(timer);
  }, [helpText, charactersRemaining]);
  const labelMaxWidth = endAdornment ? `calc(100% - ${endAdornmentWidth}px - ${SpaceXl})` : "100%";
  if (label === null || label === void 0) {
    console.error(`${COMPONENT_NAME}-error: label prop is required`);
    return null;
  }
  if (!id) {
    console.error(`${COMPONENT_NAME}-error: id prop is required`);
    return null;
  }
  if (helpText && !helpTextId) {
    console.error(
      `${COMPONENT_NAME}-error: helpText prop provided but requires helpTextId to be passed.`
    );
    return null;
  }
  const handleChange = (e) => {
    onChange == null ? void 0 : onChange(e);
  };
  const handleBlur = (e) => {
    onBlur == null ? void 0 : onBlur(e);
  };
  const HelperText = /* @__PURE__ */ jsxs(ErrorsContainer, { component: "span", children: [
    /* @__PURE__ */ jsx(
      InputError,
      {
        hasError: isError,
        errorText,
        errorTextId: errorTextId ?? "",
        errorIconA11yAttributes: {
          "aria-label": errorIconAriaLabel,
          "aria-hidden": false,
          focusable: false
        }
      }
    ),
    isMultiline && maxCharacterLimit && /* @__PURE__ */ jsx(
      StyledPlainText,
      {
        component: "span",
        color: "medium",
        variant: "t7",
        "aria-hidden": Boolean(
          maxCharacterLimit - ((value == null ? void 0 : value.length) || 0) > COUNTDOWN_START_ANNOUNCE_LIMIT
        ),
        "aria-live": "polite",
        uniqueId: `character_counter_${uniqueId}`,
        children: `${maxCharacterLimit - ((value == null ? void 0 : value.length) || 0)} ${characterCounterText}`
      }
    ),
    helpText && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        StyledPlainText,
        {
          component: "span",
          color: "medium",
          variant: "t7",
          children: helpText
        }
      ),
      isDynamicHelpText && /* @__PURE__ */ jsx(
        Box,
        {
          component: "span",
          id: helpTextId,
          ref: srOnlyRef,
          "data-testid": `srOnly-${helpTextId}`,
          sx: visuallyHidden,
          children: srOnlyHelpText
        }
      )
    ] }),
    helpLinkText && /* @__PURE__ */ jsx(StyledHelpLink, { href: helpLinkUrl, variant: "t7", children: helpLinkText })
  ] });
  const ariaDescribedBy = [helpTextId, errorTextId].filter(Boolean).join(" ").trim() || void 0;
  const ariaLabelledby = inputAriaLabelledBy || void 0;
  return /* @__PURE__ */ jsx(TextFieldMainContainer, { children: /* @__PURE__ */ jsxs(TextFieldRowContainer, { children: [
    /* @__PURE__ */ jsx(
      StyledTextField,
      {
        slotProps: {
          htmlInput: {
            id: inputId,
            name,
            autoComplete,
            inputMode,
            "aria-describedby": ariaDescribedBy,
            "aria-label": inputAriaLabel,
            "aria-labelledby": ariaLabelledby,
            "aria-required": isRequired,
            "data-testid": `input--${inputId.toLowerCase()}`,
            size: inputSize,
            maxLength: maxCharacterLimit,
            min: minValue,
            max: maxValue,
            ref: inputRef
          },
          input: {
            startAdornment: startAdornment && !isMultiline && /* @__PURE__ */ jsx(
              InputAdornment,
              {
                position: "start",
                id: startAdornmentId,
                children: startAdornment
              }
            ),
            endAdornment: endAdornment && !isMultiline && /* @__PURE__ */ jsx(
              InputAdornment,
              {
                position: "end",
                id: endAdornmentId,
                ref: endAdornmentRef,
                children: endAdornment
              }
            )
          },
          inputLabel: {
            required: false,
            id: inputLabelId || void 0,
            // This fixes storybook having an empty id tag if you remove the inputLabelId
            htmlFor: inputId,
            // Connect label to the input
            sx: {
              maxWidth: endAdornment && labelMaxWidth
            }
          }
        },
        "data-testid": getDataTestId(COMPONENT_NAME, uniqueId),
        error: isError,
        helperText: HelperText,
        id: inputId,
        label: fieldLabel,
        minRows,
        maxRows,
        multiline: isMultiline,
        onBlur: handleBlur,
        onChange: handleChange,
        onKeyDown,
        required: isRequired,
        size: "small",
        sx,
        type,
        variant: "outlined",
        value
      }
    ),
    tooltip && /* @__PURE__ */ jsx(TooltipContainer, { children: tooltip })
  ] }) });
};
TextField.displayName = COMPONENT_NAME;
export {
  TextField
};
